import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import {
	MarketStateDailySnapshotTableHeaderFragment,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateDailySnapshotTableHeaderFragment';
import React, { useEffect, useState } from 'react';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import Utilities from '@util/Utilities';
import moment from 'moment/moment';
import CalendarDay from '@app/state/calendar/CalendarDay';
import MarketStateSnapshotTableMedianFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotTableMedianFragment';
import Competitor from '@app/model/company_hotel/Competitor';
import MarketStateSnapshotHotelBasedTableRowFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotHotelBasedTableRowFragment';

export const MarketStateSnapshotHotelBasedTable: React.FunctionComponent<{
	setSelectedCalendarDay: (calendarDay: CalendarDay) => any
}> = ({ setSelectedCalendarDay }) => {

	const tools = useMarketStateTools();
	const companyHotel = tools.companyHotel;
	const { filter } = useMarketStateFilterInputTools();
	const visibleCompetitors = companyHotel.visibleCompetitors(filter.competitorTagIds);
	const [week, setWeek] = useState<number>(1);
	const weeksInMonth = Utilities.weeksInMonth(tools.calendarMonth.index.year, tools.calendarMonth.index.month);

	useEffect(() => {
		const now = moment();
		if (tools.calendarMonth.index.year === now.year() && tools.calendarMonth.index.month == Utilities.getMonthByIndex(now.month())) {
			setWeek(Utilities.weekOfMonth(now.format()));
		} else setWeek(1);
	}, [tools.calendarMonth]);

	const increaseWeek = () => {
		if (week <= weeksInMonth)
			setWeek(week + 1);
	};

	const decreaseWeek = () => {
		if (week > 1)
			setWeek(week - 1);
	};

	const days = tools.calendarMonth.days.filter(d => d.isCurrentMonth && Utilities.weekOfMonth(d.display) === week);

	const findDailySnapshot = (calendarDay: CalendarDay) => {
		return tools.marketStateSnapshot.findDailySnapshot(calendarDay.display);
	};

	const renderMainHotelRow = (days: CalendarDay[]) => {
		return <tr key={`hotelRow_${companyHotel.mainHotel.competitors[0].hotel.id}`}>
			<td
				className={Utilities.classNames('whitespace-nowrap px-3 py-4 text-sm text-gray-900 sm:pl-3 border-r-[1px] border-gray-200 font-bold')}>
				{companyHotel.mainHotel.competitors[0].hotel.name}
			</td>

			{days.map(day => {

				return <MarketStateSnapshotHotelBasedTableRowFragment key={`hotelRow_mainHotel_${day.display}`}
																															calendarDay={day} />;
			})}
		</tr>;
	};

	const renderCompetitorRow = (competitor: Competitor, days: CalendarDay[]) => {

		return <tr key={`hotelRow_${competitor.id}`}>
			<td
				className={Utilities.classNames('whitespace-nowrap px-3 py-4 text-sm text-gray-900 sm:pl-3 border-r-[1px] border-gray-200')}>
				{competitor.name}
			</td>

			{days.map(day => {
				return <MarketStateSnapshotHotelBasedTableRowFragment key={`hotelRow_${competitor.id}_${day.display}`}
																															competitor={competitor}
																															calendarDay={day} />;
			})}
		</tr>;
	};

	const renderMedianRow = (days: CalendarDay[]) => {
		return <tbody className="bg-white">
		<tr className="bg-blue-50">
			<td
				className="whitespace-nowrap px-3 py-4 text-sm font-bold text-gray-900 sm:pl-3 border-r-[1px] border-gray-200">{companyHotel.comparisonLabel()}
			</td>
			{days.map(day => <td className="whitespace-nowrap px-3 py-3 text-left" key={`cell_${day.display}`}>
				<MarketStateSnapshotTableMedianFragment key={`medianRow_${day.display}`} calendarDay={day} /></td>)}
		</tr>
		</tbody>;
	};

	return <table className="min-w-full divide-y divide-gray-300">
		<thead className="sticky-table-header">
		<tr>
			<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:pl-3">
				<div className="flex items-center">
					<span className="mr-3">Week</span>
					<span className="isolate inline-flex rounded-md shadow-sm">
											<button onClick={decreaseWeek}
															type="button"
															disabled={week === 1}
															className="relative inline-flex items-center rounded-l-md bg-white px-2 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-500 focus:z-10 disabled:cursor-not-allowed disabled:text-gray-200"
											>
													<ChevronLeftIcon className="h-4 w-4" />
											</button>
											<button
												type="button" onClick={increaseWeek}
												disabled={week === 5}
												className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-500 focus:z-10 disabled:cursor-not-allowed disabled:text-gray-200"
											>
													<ChevronRightIcon className="h-4 w-4" />
											</button>
									</span>
				</div>
			</th>
			{days.map(calendarDay => <MarketStateDailySnapshotTableHeaderFragment key={calendarDay.display}
																																						calendarDay={calendarDay}
																																						tooltipId={`table_tooltip_${calendarDay.display}`}
																																						setSelectedCalendarDay={setSelectedCalendarDay} />)}
		</tr>
		</thead>

		<tbody>
		{renderMainHotelRow(days)}
		</tbody>
		{renderMedianRow(days)}

		<tbody className="bg-white divide-y divide-gray-200">
		{visibleCompetitors.map(h => renderCompetitorRow(h, days))}
		</tbody>
	</table>;
};